import {
  CreateButton,
  Datagrid,
  EditButton,
  ExportButton,
  FilterButton,
  List,
  ShowButton,
  TopToolbar,
  useListContext,
  useResourceDefinition,
  useStore,
} from "react-admin";
import { debug_log } from "../utils/debugLog.js";
import { getStoreKey } from "../utils/getStoreKey.js";
import { getUniqueUIFilters } from "../utils/getUniqueUIFilters.js";
import {
  qualifyField,
  qualifyFilter,
  qualifyUIFilter,
  unqualifyField,
} from "../utils/qualifyFilter.js";
import { JsonStyled } from "./jsonStyled";
import { Card, CardContent } from "@mui/material";
import { Debug } from "./debug";
import omit from "lodash/omit";
import camelCase from "lodash/camelCase";
import PropTypes from "prop-types";
import { Settings } from "../utils/settings.js";
import { Empty } from "./Empty.tsx";
import FieldPreferencesButton from "./FieldPreferencesButton";

const getFrequencies = (arr) => {
  const f = {};

  arr.forEach((val) => {
    f[val] = f[val] || 0;
    f[val]++;
  });

  return f;
};

const getDuplicated = (arr) => {
  const f = getFrequencies(arr);
  return Object.keys(f).filter((k) => f[k] > 1);
};

const RemoveMissingFilters = ({ uiFilters, resource }) => {
  const { filterValues, setFilters } = useListContext();

  const storedFilterKeysQ = Object.keys(filterValues).map((storedFilterKey) =>
    qualifyField(storedFilterKey, resource)
  );
  const uiFiltersKeysQ = uiFilters.map((uiFilter) =>
    qualifyField(uiFilter.props.source, resource)
  );

  const missing = storedFilterKeysQ.filter(
    (storedFilterKeyQ) => !uiFiltersKeysQ.includes(storedFilterKeyQ)
  );

  const duplicatedQ = getDuplicated(storedFilterKeysQ);
  const duplicated = duplicatedQ.concat(
    duplicatedQ.map((k) => unqualifyField(k))
  );

  const incorrect = missing.concat(duplicated);

  if (incorrect.length) {
    debug_log(
      `RemoveMissingFilters: Removing buggy stored filters: ${incorrect.join(
        ", "
      )}.`
    );
    const sanitizedFilters = omit(filterValues, incorrect);
    setFilters(sanitizedFilters);
  }

  return null;
};

const camelizeKeys = (o) => {
  if (!o) {
    return o;
  }

  const camel = {};

  Object.keys(o).forEach((key) => {
    camel[key] = o[key];
    if (key.includes("_")) {
      camel[camelCase(key)] = o[key];
    }
  });

  return camel;
};

const ListActions = function (props) {
  return (
    <TopToolbar>
      <FieldPreferencesButton
        resource={props.resource}
        fields={props.fields}
        onPreferencesUpdate={props.onPreferencesUpdate}
      />
      <FilterButton />
      {props.resourceData.hasCreate && (
        <CreateButton state={{ record: props.filter }} />
      )}
      <ExportButton />
    </TopToolbar>
  );
};

const getRowButtons = ({ resourceData, showOnClick }) => {
  const buttons = [];

  if (resourceData.hasShow && !showOnClick) {
    buttons.push(<ShowButton key="show" />);
  }

  if (resourceData.hasEdit) {
    buttons.push(<EditButton key="edit" />);
  }

  return buttons;
};

export const MyList = (props) => {
  const resource = props.resource;
  const resourceData = useResourceDefinition({ resource });

  const Grid = Datagrid;

  debug_log(`<<<<<<<<<<<< List for '${resource}' <<< start`);

  let filters = getUniqueUIFilters(props.filters, props.filter);
  filters = qualifyUIFilter(filters, resource);

  const filter = qualifyFilter(props.filter, resource);
  const storeKey = getStoreKey({ resource });
  const [storedData] = useStore(storeKey);
  const showOnClick = Settings.getValue("showOnClick");

  const dataGridProps = Object.assign(
    {},
    { rowClick: resourceData.hasShow && showOnClick ? "show" : null },
    props.dataGridProps || {}
  );

  const processedProps = Object.assign({}, props, {
    filter,
    filters,
    storeKey,
    dataGridProps: null,
    perPage: 25,
    empty: (
      <Empty buttonProps={{ state: { record: camelizeKeys(props.filter) } }} />
    ),
  });

  const actionsProps = {
    resource,
    resourceData,
    filter: camelizeKeys(props.filter),
    fields: props.fields,
    onPreferencesUpdate: props.onPreferencesUpdate,
  };

  const actions = <ListActions {...actionsProps} />;

  debug_log(`>>>>>>>>>>>> List for '${resource}' >>> end`);

  return (
    <>
      <List {...processedProps} actions={actions}>
        <Grid {...dataGridProps} bulkActionButtons={props.bulkActionButtons}>
          {props.children}
          {getRowButtons({ resourceData, showOnClick })}
        </Grid>
        <RemoveMissingFilters uiFilters={filters} resource={resource} />
      </List>
      <Debug>
        <Card>
          <CardContent>
            <h3>Fixed filter</h3>
            <JsonStyled data={filter} />
            <h3>List setup</h3>
            <JsonStyled
              data={{
                resource,
                resourceData,
                storeKey,
                storedData,
                showOnClick,
              }}
            />
          </CardContent>
        </Card>
      </Debug>
    </>
  );
};

MyList.propTypes = {
  resource: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  onPreferencesUpdate: PropTypes.func.isRequired,
};
